
import { useCallback, useEffect, useMemo, useState } from 'react'
import { practitionerRoutes } from '../utils/integrationConfig'
import {
  PractitionerApiError,
  createPractitionerAccount,
  describeError,
  signInPractitioner,
  startPractitionerSubscription,
} from '../utils/practitionerApi'
import type {
  MembershipPlanId,
  PractitionerAccountInput,
  PractitionerSession,
  SignupStep,
} from '../types/practitionerSignup'

import type { PractitionerSubscriptionResponse } from '../utils/practitionerApi'

function stepFromPath(path: string): SignupStep {
  return path.startsWith(practitionerRoutes.membership)
    ? 'membership'
    : 'account'
}

function isSessionValid(
  session: PractitionerSession | null,
): session is PractitionerSession {
  // Treat tokens within 60s of expiry as expired.
  return Boolean(session && session.expiresAt - 60_000 > Date.now())
}

/**
 * State for /practitioners/join → /practitioners/membership.
 *
 * The website:
 * 1. Creates the practitioner's Supabase account.
 * 2. Signs the practitioner in.
 * 3. Creates the selected Stripe subscription.
 * 4. Receives the Stripe SetupIntent client secret.
 * 5. Passes the subscription information to the membership page,
 *    where Stripe payment-method setup can be completed.
 *
 * The practitioner onboarding/profile flow remains in the mobile app.
 */
export function usePractitionerSignup(initialStep: SignupStep) {
  const [step, setStep] = useState<SignupStep>(initialStep)
  const [session, setSession] =
    useState<PractitionerSession | null>(null)

  const [selectedPlan, setSelectedPlan] =
    useState<MembershipPlanId | null>(null)

  const [accountSubmitting, setAccountSubmitting] =
    useState(false)

  const [accountError, setAccountError] =
    useState<PractitionerApiError | null>(null)

  const [needsSignIn, setNeedsSignIn] =
    useState(false)

  const [signInSubmitting, setSignInSubmitting] =
    useState(false)

  const [signInError, setSignInError] =
    useState<string | null>(null)

  /**
   * Plan currently being submitted to the subscription Edge Function.
   *
   * This keeps the existing MembershipPlans component compatible.
   */
  const [redirectingPlan, setRedirectingPlan] =
    useState<MembershipPlanId | null>(null)

  const [checkoutError, setCheckoutError] =
    useState<string | null>(null)

  /**
   * Stripe subscription information returned by the backend.
   *
   * This is kept in React memory only.
   */
  const [subscription, setSubscription] =
    useState<PractitionerSubscriptionResponse | null>(null)

  /**
   * Kept for backwards compatibility with the current page.
   *
   * There is no hosted Stripe Checkout redirect in the new flow,
   * so this will normally be false.
   */
  const checkoutCancelled = useMemo(
    () =>
      new URLSearchParams(window.location.search).get('checkout') ===
      'cancelled',
    [],
  )

  useEffect(() => {
    const onPopState = () =>
      setStep(stepFromPath(window.location.pathname))

    window.addEventListener('popstate', onPopState)

    return () =>
      window.removeEventListener('popstate', onPopState)
  }, [])

  const goTo = useCallback((next: SignupStep) => {
    const path =
      next === 'membership'
        ? practitionerRoutes.membership
        : practitionerRoutes.join

    if (window.location.pathname !== path) {
      window.history.pushState(null, '', path)
    }

    setStep(next)
    window.scrollTo({ top: 0 })
  }, [])

  /**
   * Creates the Stripe subscription for the selected plan.
   *
   * IMPORTANT:
   * This does NOT redirect to Stripe Checkout.
   *
   * The backend creates the subscription and returns the SetupIntent
   * client secret. The membership page will use that client secret
   * with Stripe.js to securely collect/save the payment method.
   */
  const beginSubscription = useCallback(
    async (
      planId: MembershipPlanId,
      activeSession: PractitionerSession,
    ) => {
      setCheckoutError(null)
      setRedirectingPlan(planId)
      setSubscription(null)

      try {
        const subscriptionResponse =
          await startPractitionerSubscription(
            planId,
            activeSession,
          )

        setSubscription(subscriptionResponse)
      } catch (error) {
        setCheckoutError(describeError(error))
      } finally {
        setRedirectingPlan(null)
      }
    },
    [],
  )

  const submitAccount = useCallback(
    async (input: PractitionerAccountInput) => {
      setAccountSubmitting(true)
      setAccountError(null)

      try {
        await createPractitionerAccount(input)
      } catch (error) {
        /*
         * Do not use `instanceof PractitionerApiError` here.
         *
         * The Magic Patterns runtime can load the class in a way that
         * makes instanceof fail with:
         * "Right-hand side of 'instanceof' is not an object".
         *
         * Instead, check the error shape safely.
         */
        if (
          error &&
          typeof error === 'object' &&
          'code' in error &&
          'message' in error
        ) {
          setAccountError(
            new PractitionerApiError(
              String(error.code),
              String(error.message),
            ),
          )
        } else {
          setAccountError(
            new PractitionerApiError(
              'server',
              describeError(error),
            ),
          )
        }

        setAccountSubmitting(false)
        return
      }

      /*
       * Account exists now.
       *
       * Sign in immediately so the authenticated JWT can be used
       * when creating the Stripe subscription.
       */
      try {
        const nextSession = await signInPractitioner(
          input.email,
          input.password,
        )

        setSession(nextSession)
      } catch (error) {
        /*
         * For example, email confirmation may be required.
         * The practitioner can sign in from the membership step.
         */
        setNeedsSignIn(true)
        setSignInError(describeError(error))
      }

      setAccountSubmitting(false)
      goTo('membership')
    },
    [goTo],
  )

  /**
   * Practitioner selects a membership plan.
   */
  const choosePlan = useCallback(
    (planId: MembershipPlanId) => {
      setSelectedPlan(planId)
      setCheckoutError(null)
      setSubscription(null)

      /*
       * If the session is missing or close to expiry, ask the
       * practitioner to sign in before creating the subscription.
       */
      if (!isSessionValid(session)) {
        setNeedsSignIn(true)
        return
      }

      void beginSubscription(planId, session)
    },
    [session, beginSubscription],
  )

  /**
   * Sign in from the membership page and then resume the selected
   * membership plan.
   */
  const resumeWithSignIn = useCallback(
    async (email: string, password: string) => {
      setSignInSubmitting(true)
      setSignInError(null)

      try {
        const nextSession = await signInPractitioner(
          email,
          password,
        )

        setSession(nextSession)
        setNeedsSignIn(false)

        if (selectedPlan) {
          await beginSubscription(
            selectedPlan,
            nextSession,
          )
        }
      } catch (error) {
        setSignInError(describeError(error))
      } finally {
        setSignInSubmitting(false)
      }
    },
    [selectedPlan, beginSubscription],
  )

  /**
   * Clear the current subscription response.
   *
   * Useful if the practitioner changes plans before completing
   * payment-method setup.
   */
  const clearSubscription = useCallback(() => {
    setSubscription(null)
    setCheckoutError(null)
  }, [])

  return {
    step,
    goTo,

    session,

    selectedPlan,

    accountSubmitting,
    accountError,
    submitAccount,

    choosePlan,

    redirectingPlan,

    checkoutError,
    checkoutCancelled,

    /**
     * Stripe subscription information returned by Supabase.
     *
     * Important fields include:
     * - setupIntentClientSecret
     * - subscriptionId
     * - customerId
     * - status
     * - tierName
     * - planType
     * - amountBilled
     * - trialEndDate
     */
    subscription,

    clearSubscription,

    needsSignIn,
    signInSubmitting,
    signInError,
    resumeWithSignIn,

    cancelSignIn: () => setNeedsSignIn(false),
  }
}
